import React, { useCallback, useEffect, useMemo, useState } from "react";
import usePermissions from "commons/hooks/usePermissions";
import useFrontDeskData from "./useFrontDeskData";
import { Box, Fab, Grid, Zoom } from "@material-ui/core";
import FacilityProductsCard from "./cards/FacilityProductsCard";
import EntityCard from "./cards/EntityCard";
import CategoryTree from "../sales/components/CategoryTree";
import Stack from "commons/components/Stack";
import FormTextField from "commons/components/FormTextField";
import {
  add,
  append,
  assoc,
  compose,
  concat,
  equals,
  groupBy,
  mergeWith,
  prop,
  propEq,
  __,
} from "ramda";
import TotalsCard from "./cards/TotalsCard";
import PaymentCard from "./cards/PaymentCard";
import LinesCard from "./cards/LinesCard";
import {
  applyLineExtras,
  calculateModel,
  isForOp,
  lineFromProduct,
} from "./utils";
import { Save } from "@material-ui/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory, useLocation, useParams } from "react-router";
import api from "commons/helpers/api";
import dayjs from "dayjs";
import CloseCard from "../sales/components/CloseCard";
import ReceiptCard from "./cards/ReceiptCard";
import StocksPrintCard from "./cards/StocksPrintCard";
import PageCard from "commons/components/PageCard";
import ExtrasCard from "./cards/ExtrasCard";
import FrontdeskToolbar from "./FrontdeskToolbar";
import { rulesReducer, sumField } from "commons/helpers/utils";
import ErrorAlert from "commons/components/ErrorAlert";
import { prepareDiscount, prepareTax } from "./utils/initializers";
import StocksCard from "./cards/StocksCard";
import { Alert, AlertTitle } from "@material-ui/lab";
import useTranslate from "commons/hooks/useTranslate";
import { useFrontDeskStocks } from "./useFrontDeskStocks";

const fetchResource = ({ queryKey }) => {
  const [base, id] = queryKey;
  return api.service(base).get(id);
};

const isValidationError = (error) => {
  return error.code === 422 && Array.isArray(error.data);
};

const getInitial = (extras = {}) => {
  const facility_id =
    parseInt(window.localStorage.getItem("fdDefaultFacility")) || null;
  const access_group_id =
    parseInt(window.localStorage.getItem("defaultAccessGroup")) || null;
  return {
    facility_id,
    access_group_id,
    date: dayjs(),
    payments: [],
    lines: [],
    stocks: [],
    returns: [],
    discounts: [],
    taxes: [],
    commissions: [],
    total: 0,
    subtotal: 0,
    ...extras,
  };
};

export default function FrontDeskController({ base = "sales" }) {
  const { id } = useParams();
  const client = useQueryClient();
  const history = useHistory();
  const location = useLocation();
  const [rules, setRules] = useState({});
  // Read model from url query.
  const queryModel = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const initial = q.get("model");
    return JSON.parse(initial) ?? {};
  }, [location]);
  const [model, setModel] = useState(() => {
    if (id === "create") return getInitial(queryModel);
    const d = client.getQueryData([base, parseInt(id)]);
    return d ? d : getInitial();
  });
  const [dirty, setDirty] = useState(false);
  const isCreating = id === "create";

  useEffect(() => {
    if (isCreating && Boolean(model.id)) {
      setModel(getInitial(queryModel));
    }
  }, [model.id, queryModel, isCreating]);

  const { error: queryError } = useQuery([base, parseInt(id)], fetchResource, {
    enabled: !isCreating,
    onSuccess: setModel,
  });

  const onDataChange = useCallback(
    (obj) => {
      if (model.id === obj.id && !equals(model, obj)) {
        client.invalidateQueries([base, model.id]);
      }
    },
    [client, model, base]
  );

  useEffect(() => {
    api.service(base).on("updated", onDataChange);
    api.service(base).on("patched", onDataChange);
    api.service(base).on("removed", onDataChange);
    return () => {
      api.service(base).removeListener("updated", onDataChange);
      api.service(base).removeListener("patched", onDataChange);
      api.service(base).removeListener("removed", onDataChange);
    };
  }, [base, onDataChange]);

  const { mutate, error: mutateError } = useMutation(
    ({ base, model }) => {
      if (!Boolean(model.id)) {
        return api.service(base).create(model);
      } else {
        return api.service(base).update(model.id, model);
      }
    },
    {
      onError: (error) => {
        setDirty(true);
        if (isValidationError(error)) {
          setRules(error.data.reduce(rulesReducer, {}));
        }
      },
      onSuccess: (data) => {
        if (id === "create") {
          client.setQueryData([base, data.id], data);
          history.push(`/s/${base}/${data.id}`);
        }
      },
    }
  );

  const onModelChange = (model) => {
    setDirty(true);
    setModel(calculateModel(base)(model));
  };

  const onSave = (onSaveSuccess = () => {}, m) => {
    if (dirty) {
      setDirty(false);
      setRules({});
      mutate(
        { base, model: m || model },
        {
          onSuccess: onSaveSuccess,
        }
      );
    } else {
      onSaveSuccess();
    }
  };

  return (
    <Frontdesk
      sales={base === "sales"}
      dirty={dirty}
      model={model}
      onChange={onModelChange}
      onSave={onSave}
      rules={rules}
      queryError={queryError}
      mutateError={mutateError}
      isCreating={isCreating}
    />
  );
}

function useToggle(name) {
  const [toggle, setToggle] = useState(
    () => JSON.parse(window.localStorage.getItem(name)) || false
  );

  const onToggle = () => {
    setToggle(!toggle);
    window.localStorage.setItem(name, !toggle);
  };

  return [toggle, onToggle];
}

const pairsMatch = (p1, p2) => p1[0] === p2[0] && p1[1] === p2[1];

function Frontdesk({
  model,
  onChange,
  onSave,
  sales = true,
  dirty = false,
  isCreating = false,
  rules = {},
  queryError,
  mutateError,
  ReceiptExtras = null, //TODO check what this does
  tables = [], // TODO get from usefrontdeskdata
}) {
  const {
    facilities,
    products,
    policies,
    locations,
    settings,
    discounts,
    commissions,
    taxes,
    employees,
    components,
    stockLevels,
  } = useFrontDeskData();
  const { t } = useTranslate();
  const { hasPermission } = usePermissions();
  const [showCategories, toggleShowCategories] = useToggle("fdShowCategories");
  const [compactView, toggleCompactView] = useToggle("fdCompactView");
  const payment_field = sales ? "customer_id" : "supplier_id";
  const [appliedActiveExtras, setAppliedActiveExtras] = useState([0, 0]);

  const onPayment = compose(
    onChange,
    assoc("payments", __, model),
    append(__, model.payments),
    assoc(payment_field, model[payment_field])
  );
  useEffect(() => {
    if (model.payments.some((p) => !Boolean(p.id))) {
      onSave();
    }
  }, [onSave, model.payments]);

  useEffect(() => {
    if (sales && isCreating) {
      const currD = discounts.filter(isForOp(model)).map(prepareDiscount);
      const currT = taxes.filter(isForOp(model)).map(prepareTax);
      const pair = [currD.length, currT.length];
      if (!pairsMatch(appliedActiveExtras, pair)) {
        setAppliedActiveExtras(pair);
        onChange({ ...model, discounts: currD, taxes: currT });
      }
    }
  }, [
    sales,
    isCreating,
    discounts,
    taxes,
    model,
    onChange,
    appliedActiveExtras,
  ]);

  useEffect(() => {
    if (model.policy_id) {
      setAppliedActiveExtras([0, 0]);
    }
  }, [model.policy_id]);

  const filteredProducts = useMemo(
    () =>
      products.filter(
        (prod) =>
          prod.facility_id === model.facility_id &&
          prod[sales ? "saleable" : "purchasable"] === true
      ),
    [sales, products, model.facility_id]
  );

  const canDoAction = useCallback(hasPermission(model.access_group_id), [
    hasPermission,
    model.access_group_id,
  ]);

  const allStocks = useMemo(() => {
    const byProductID = groupBy(prop("product_id"));
    const lineStocks = model.lines
      .flatMap((l) => l.stocks)
      .filter((s) => !Boolean(s.operation_line_id));
    return mergeWith(concat, stockLevels, byProductID(lineStocks));
  }, [stockLevels, model.lines]);

  const productOwnStocks = useMemo(() => {
    return products.reduce(
      (acc, { product_id }) => ({
        ...acc,
        [product_id]: sumField("quantity")(allStocks[product_id] || []),
      }),
      {}
    );
  }, [allStocks, products]);

  const productCompStocks = useMemo(() => {
    return products.reduce((acc, p) => {
      const myComponents = components.filter((c) => {
        return Number(c.product_id) === Number(p.product_id);
      });
      const compStocks = myComponents.map((comp) => {
        const { component_id, product_ratio, component_ratio } = comp;
        const total = productOwnStocks[component_id] || 0;
        return total * (product_ratio / component_ratio);
      });
      return {
        ...acc,
        [p.product_id]:
          myComponents.length > 0 ? Math.floor(Math.min(...compStocks)) : 0,
      };
    }, {});
  }, [productOwnStocks, products, components]);

  const stockTotals = useMemo(
    () => mergeWith(add, productOwnStocks, productCompStocks),
    [productOwnStocks, productCompStocks]
  );

  const {
    error,
    updateLineField,
    mergeLineFields,
    onChangeLineQuantity,
    onChangeQuantity,
    onChangeReturned,
    onDeleteLine,
  } = useFrontDeskStocks({
    sales,
    model,
    onChange,
    products,
    components,
    settings,
    canDoAction,
    productOwnStocks,
    productCompStocks,
    stockTotals,
    stockLevels,
  });

  const onProductSelect = (product_id, qty = 1) => {
    const isSameProduct = propEq("product_id", product_id);
    const index = model.lines.findIndex(isSameProduct);
    const product = products.find(isSameProduct);
    if (!product) return;

    if (index !== -1) {
      const line = model.lines[index];
      onChangeQuantity(line.id)(Number(line.quantity) + Number(qty));
    } else {
      const line = sales
        ? applyLineExtras(
            discounts,
            taxes,
            commissions,
            model,
            product
          )(lineFromProduct(product, policies, model.policy_id, sales))
        : lineFromProduct(product, policies, model.policy_id, sales);
      onChangeLineQuantity([...model.lines, line])(line.id)(qty);
    }
  };

  return (
    <PageCard>
      <FrontdeskToolbar
        sales={sales}
        model={model}
        onChange={onChange}
        toggleShowCategories={toggleShowCategories}
        toggleCompactView={toggleCompactView}
      />
      <br />
      <ErrorAlert error={error ? { message: error } : null} />
      <ErrorAlert error={queryError} />
      <ErrorAlert error={mutateError} />
      {rules.lines && (
        <Alert elevation={6} variant="filled" severity="error">
          <AlertTitle>{t("mustHaveLines")}</AlertTitle>
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <FacilityProductsCard
            model={model}
            onChange={onChange}
            facilities={facilities}
            products={filteredProducts}
            onProductSelect={onProductSelect}
            sales={sales}
            compact={compactView}
            rules={rules}
          />
        </Grid>
        <Grid item xs={4}>
          <EntityCard
            model={model}
            onChange={onChange}
            isCreating={isCreating}
            base={sales ? "customers" : "suppliers"}
            compact={compactView}
            rules={rules}
            policies={policies}
            products={products}
          />
        </Grid>
        {showCategories && (
          <CategoryTree
            base={sales ? "sales" : "purchases"}
            products={filteredProducts}
            onProductSelect={onProductSelect}
          />
        )}
        <Grid item sm={9}>
          <Stack>
            <LinesCard
              sales={sales}
              model={model}
              locations={locations}
              discounts={discounts}
              taxes={taxes}
              commissions={commissions}
              canDoAction={canDoAction}
              products={filteredProducts}
              totals={stockTotals}
              updateLineField={updateLineField}
              mergeLineFields={mergeLineFields}
              onChangeQuantity={onChangeQuantity}
              onChangeReturned={onChangeReturned}
              onDeleteLine={onDeleteLine}
            />
            <Grid container spacing={2}>
              <Grid item sm={4}>
                <ReceiptCard
                  model={model}
                  base={sales ? "sales" : "purchases"}
                  tables={tables}
                  Extras={ReceiptExtras}
                  onPrintRequested={onSave}
                />
              </Grid>
              <Grid item sm={4}>
                <StocksPrintCard
                  model={model}
                  onPrintRequested={onSave}
                  products={products}
                  facilities={facilities}
                  printComponents={settings["printComponents"]}
                  autoFulfilOnPrint={settings["autoFulfilOnPrint"]}
                />
              </Grid>
              <Grid item sm={4}>
                <CloseCard
                  value={model.closed}
                  onChange={compose(onChange, assoc("closed", __, model))}
                />
              </Grid>
            </Grid>
            <StocksCard
              model={model}
              onChange={onChange}
              products={products}
              facilities={facilities}
              manualStockChange={!settings["autoStockChange"]}
              printComponents={settings["printComponents"]}
              canDoAction={canDoAction}
            />
          </Stack>
        </Grid>
        <Grid item sm={3}>
          <Stack>
            <TotalsCard
              model={model}
              showCost={canDoAction("show-operation-cost")}
              showProfit={sales && canDoAction("show-operation-profit")}
              compact={compactView}
            />
            <PaymentCard model={model} onPayment={onPayment} />
            <ExtrasCard
              items={model.discounts}
              options={discounts}
              title="discounts"
              onChange={compose(onChange, assoc("discounts", __, model))}
              canDoAction={canDoAction}
            />
            <ExtrasCard
              items={model.taxes}
              options={taxes}
              title="taxes"
              onChange={compose(onChange, assoc("taxes", __, model))}
              canDoAction={canDoAction}
            />
            <ExtrasCard
              items={model.commissions}
              options={employees}
              title="commissions"
              onChange={compose(onChange, assoc("commissions", __, model))}
              canDoAction={canDoAction}
            />
          </Stack>
        </Grid>
        <FormTextField
          multiline
          label="notes"
          value={model.notes}
          onChange={compose(onChange, assoc("notes", __, model))}
          error={rules.reference}
        />
      </Grid>
      <Box position="fixed" bottom={24} right={24} className="no-print">
        <Zoom in style={{ transitionDelay: "500ms" }}>
          <Fab
            type="submit"
            color="primary"
            onClick={() => onSave()}
            disabled={!dirty}
            id="resource-save-btn"
          >
            <Save />
          </Fab>
        </Zoom>
      </Box>
    </PageCard>
  );
}
